import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Typography, useMediaQuery } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service';
import ReportPeriodService, { IConversionYearJob } from '../../../shared/services/report-period/report-period.service';
import { IConversionYear } from '../../../shared/services/conversion-source/conversion-source.model';
import { TableDisplay } from '../miscellaneous-components/table-display';
import { RecalculationHistoryTable } from './recalculation-history-table';
import { LoadingSpinner } from '../display-elements-components/loading-spinner';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';

interface ConversionFactorRow {
  id: string;
  source: string;
  conversionType: string;
  conversionSourceId: string;
  allYears?: string[];
}

interface ReportPeriodConversionFactorsTableProps {
  reportPeriodId: string;
  conversionFiguresYear: string;
  locked?: boolean;
  onChangeYear: (conversionSourceId: string, conversionSourceName: string, availableYears: string[]) => void;
}

export const ReportPeriodConversionFactorsTable: React.FC<ReportPeriodConversionFactorsTableProps> = ({
  reportPeriodId,
  conversionFiguresYear,
  locked = false,
  onChangeYear,
}) => {
  const [rows, setRows] = useState<ConversionFactorRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [orderBy, setOrderBy] = useState<string>('source');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [changeHistory, setChangeHistory] = useState<IConversionYearJob[]>([]);

  const conversionSourceService = new ConversionSourceService();
  const reportPeriodService = new ReportPeriodService();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    fetchConversionFactors();
    // conversionFiguresYear changes when a year change completes and the page
    // refetches the period, so the history refreshes with it.
    reportPeriodService.getConversionYearJobs(reportPeriodId)
      .then(response => setChangeHistory(response.jobs || []))
      .catch(() => setChangeHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodId, conversionFiguresYear]);

  const fetchConversionFactors = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      const sources = await conversionSourceService.getAll();

      if (!sources || sources.length === 0) {
        setRows([]);
        return;
      }

      const rowsData: ConversionFactorRow[] = [];

      for (const source of sources) {
        if (source.id) {
          try {
            const sourceWithYears = await conversionSourceService.getOne(source.id, '?includeYears=true');
            const allYears: string[] = [];

            if (sourceWithYears && sourceWithYears.conversionYears) {
              sourceWithYears.conversionYears.forEach((y: IConversionYear) => {
                if (y.year && !allYears.includes(y.year)) {
                  allYears.push(y.year);
                }
              });
              allYears.sort();
            }

            rowsData.push({
              id: source.id,
              source: source.source || 'Unknown',
              conversionType: source.conversionType || 'Unknown',
              conversionSourceId: source.id,
              allYears,
            });
          } catch (error) {
            console.error(`Failed to fetch years for source ${source.id}:`, error);
            rowsData.push({
              id: source.id,
              source: source.source || 'Unknown',
              conversionType: source.conversionType || 'Unknown',
              conversionSourceId: source.id,
              allYears: [],
            });
          }
        }
      }

      setRows(rowsData);
    } catch (error) {
      console.error('Failed to fetch conversion factors:', error);
      setErrorMessage('Failed to load conversion factors. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeYear = (row: ConversionFactorRow) => {
    onChangeYear(row.conversionSourceId, row.source, row.allYears || []);
  };

  const getConversionTypeFriendlyName = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      conversion: 'Activity Based',
      spendBasedConversion: 'Spend Based',
      squareFootConversion: 'Square Foot',
      residualFuelMix: 'Residual Fuel Mix',
    };
    return typeMap[type] || type;
  };

  const headCells: IHeadCell[] = [
    {
      id: 'source',
      label: 'Conversion Factor Source',
      sortable: true,
      numeric: false,
    },
    {
      id: 'conversionType',
      label: 'Conversion Type',
      sortable: false,
      numeric: false,
    },
    {
      id: 'action',
      label: 'Action',
      sortable: false,
      numeric: false,
    },
  ];

  const tableRows = rows.map((row) => {
    const hasAvailableYears = row.allYears && row.allYears.length > 0;

    return {
      ...row,
      conversionType: (
        <Chip
          label={getConversionTypeFriendlyName(row.conversionType)}
          size="small"
          variant="outlined"
        />
      ),
      action: (
        // The span carries the title: disabled MUI buttons get pointer-events none,
        // so a title on the button itself would never show.
        <span title={locked ? 'Report period is locked; reopen it to change the conversion year' : 'Change conversion year'}>
          <IconButton
            size="small"
            onClick={() => handleChangeYear(row)}
            disabled={locked || !hasAvailableYears}
            sx={{
              color: !locked && hasAvailableYears ? gaiaTheme.palette.primary.main : 'lightgray',
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </span>
      ),
    };
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{errorMessage}</Typography>
        <Button onClick={fetchConversionFactors} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">No conversion sources available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableDisplay
        headCells={headCells}
        rows={tableRows}
        selected={selected}
        setSelected={setSelected}
        onOrderChange={(orderBy: string, order: 'asc' | 'desc') => {
          setOrderBy(orderBy);
          setOrder(order);
        }}
        inputPage={inputPage}
        setInputPage={setInputPage}
        totalRecords={rows.length}
        perPage={perPage}
        setPerPage={setPerPage}
        isMobileMode={isMobileMode}
      />
      <RecalculationHistoryTable
        title='Factor year change history'
        changeLabel='Factor source and new year'
        rows={changeHistory.map((job) => ({
          id: job.id,
          createdAt: job.createdAt,
          change: `${job.conversionSourceName ?? 'Conversion source'} to ${job.newYear}`,
          status: job.status,
          outcome: job.status === 'COMPLETED' ? `${job.recalculatedCount} ${job.recalculatedCount === 1 ? 'calculation' : 'calculations'} updated` : null,
          errorMessage: job.errorMessage,
          beforeTotals: job.beforeTotals,
          afterTotals: job.afterTotals,
        }))}
      />
    </Box>
  );
};
